import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { IconButton } from '@ui/button';
import { Radio } from '@web/atoms';
import { useShopifyCart } from '@web/shopify';
import { ResponsiveImage } from '@web/molecules/ResponsiveImage';
import { FormFieldsContainer, FormWrapper } from '../FormFieldsContainer';
import { StyledFormWithImage } from '../StyledForm';
import { Question } from '../Question';
import { ApiResult } from '../../api';
import { useReporter } from '../../reporter';
import { useSessionCustomer } from '../../customer';
import { onboardingSubmitted, onboardingBackArrowClicked } from '../events';
import {
  FieldWrapper,
  ImageContainer,
  ButtonContainer,
  MobileImageContainer,
  DesktopImageContainer,
} from './styles.js';
import { schema } from './schema';

export { schema } from './schema';

export const PupStatusForm = ({
  defaultValues,
  goBack,
  labelName,
  index,
  onSubmit,
  pupStatusQuestionSubheadline,
  pupStatusQuestionPrompt,
  spayedChipLabel,
  unspayedChipLabel,
  mobileImage,
  desktopImage,
}) => {
  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isSubmitted, isSubmitSuccessful },
    reset,
    getValues,
  } = useForm({
    defaultValues,
    resolver: yupResolver(schema),
    mode: 'onTouched',
  });

  const isInSubmittingState =
    isSubmitting || (isSubmitted && isSubmitSuccessful);

  const reporter = useReporter();
  const { customer } = useSessionCustomer();
  const { cart } = useShopifyCart();

  const statusList = [
    { name: spayedChipLabel, value: 'true' },
    { name: unspayedChipLabel, value: 'false' },
  ];

  const goBackWithReporter = () => {
    reporter.tag(
      onboardingBackArrowClicked(
        'pup_fixed_status_step',
        cart?.customer || customer,
      ),
    );
    goBack();
  };

  const submitForm = async data => {
    reporter.tag(
      onboardingSubmitted(
        index,
        'dog_fixed_status',
        cart?.customer || customer,
        data,
      ),
    );
    const result = await onSubmit(data);

    ApiResult.ifError(result, () =>
      setTimeout(() =>
        reset(getValues(), {
          keepIsSubmitted: false,
        }),
      ),
    );
  };
  return (
    <StyledFormWithImage>
      <ImageContainer>
        <ButtonContainer aria-live="polite">
          <IconButton
            aria-label="go to previous step"
            icon={{
              iconName: 'arrow-left',
              size: '24px',
              alt: 'back',
              id: 'prev_step_footer',
              color: 'background.lightest',
            }}
            onClick={goBackWithReporter}
            disabled={isInSubmittingState}
            type="button"
            variant="dark"
          />
        </ButtonContainer>
        <MobileImageContainer>
          <ResponsiveImage priority={true} {...mobileImage} width={400} />
        </MobileImageContainer>
        <DesktopImageContainer>
          <ResponsiveImage
            priority={true}
            {...desktopImage}
            height={362}
            width={406}
          />
        </DesktopImageContainer>
      </ImageContainer>
      <FormWrapper $minHeight={'280'}>
        <FormFieldsContainer $maxWidth={391}>
          <Question
            subheadline={pupStatusQuestionSubheadline}
            prompt={pupStatusQuestionPrompt}
            typography={'heading4'}
            gap={0}
          >
            <FieldWrapper>
              <Controller
                control={control}
                name="spayedStatus"
                render={({
                  field: { ref, onChange, ...field },
                  fieldState: { error },
                }) => (
                  <>
                    <Radio
                      {...field}
                      variant="sea"
                      showRadioIcon={false}
                      choices={statusList}
                      legendText={labelName}
                      name="fixed_status"
                      error={error?.message || ''}
                      status={error?.message ? 'error' : null}
                      onChange={value => {
                        onChange(value);
                        handleSubmit(submitForm)();
                      }}
                      onClick={event => {
                        const clickedValue = event?.target?.value;
                        /*this handles if a person goes back and selects the same option*/
                        if (field.value === clickedValue) {
                          handleSubmit(submitForm)();
                        }
                      }}
                    />
                  </>
                )}
              />
            </FieldWrapper>
          </Question>
        </FormFieldsContainer>
      </FormWrapper>
    </StyledFormWithImage>
  );
};

PupStatusForm.displayName = 'PupStatusForm';

PupStatusForm.propTypes = {
  defaultValues: PropTypes.shape({
    spayedStatus: PropTypes.string,
  }).isRequired,
  goBack: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  labelName: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  pupStatusQuestionSubheadline: PropTypes.string.isRequired,
  pupStatusQuestionPrompt: PropTypes.string.isRequired,
  ctaButtonText: PropTypes.string,
  mobileImage: PropTypes.object.isRequired,
  desktopImage: PropTypes.object.isRequired,
};
